import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { COLORS } from '../theme';

// HC-RATING-004 follow-on: superadmin review queue for AI-flagged
// comment/score inconsistencies. Modeled directly on ReferralReviewsPage.js's
// pattern (real-time onSnapshot on reviewStatus == 'pending', deduplicated
// secondary getDoc fetches, modal-driven Approve/Deny, nav badge) — see
// hc-rating-004-comment-score-review-queue-investigation.md's proposed UI
// scope section for the design this was built from.
//
// Unlike ReferralReviewsPage (where every item requires a human decision by
// design), this queue only ever shows AI-flagged exceptions —
// ratingConsistencyReviews docs are only ever created for 'inconsistent'
// verdicts (runRatingModerationPass, rating-triggers.ts); a 'consistent'
// verdict never creates a document here at all. So there's no AI-advisory
// qualify/deny/review column the way referrals has — the AI verdict itself
// (always 'inconsistent' in this queue, by construction) is the reason the
// row exists, not a recommendation alongside a separate human judgment call.

const COL = '1.3fr 1.3fr 85px 110px 1.6fr 1.3fr 140px';
const HEADERS = ['Rater', 'Ratee', 'Rating Date', 'Scores', 'Comment', 'AI Verdict', 'Actions'];

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function truncate(text, max) {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '…' : text;
}

function Skeleton() {
  return (
    <div style={{ height: '13px', backgroundColor: COLORS.surfaceHover, borderRadius: '4px', width: '70%', animation: 'pulse 1.4s ease-in-out infinite' }} />
  );
}

function ScoresCell({ answers }) {
  if (!answers || answers.length === 0) return <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>—</span>;
  const avg = answers.reduce((sum, a) => sum + (Number(a.value) || 0), 0) / answers.length;
  return (
    <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
      {avg.toFixed(1)} avg <span style={{ color: COLORS.textMuted }}>({answers.length})</span>
    </span>
  );
}

function VerdictCell({ onToggle }) {
  return (
    <span
      onClick={e => { e.stopPropagation(); onToggle(); }}
      style={{ cursor: 'pointer', color: COLORS.warning, fontSize: '12px', fontWeight: '600', userSelect: 'none' }}
    >
      INCONSISTENT
      <span style={{ marginLeft: '4px', fontSize: '10px', color: COLORS.textMuted }}>▼</span>
    </span>
  );
}

const INPUT_STYLE = {
  color: '#1F2937', backgroundColor: 'white', border: '1px solid #D1D5DB',
  padding: '8px 12px', borderRadius: '6px', fontSize: '14px',
  width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
};

export default function RatingConsistencyReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [raterNames, setRaterNames] = useState({});
  const [ratedUserNames, setRatedUserNames] = useState({});
  const [ratingDetails, setRatingDetails] = useState({}); // { [ratingId]: { submittedAt, answers, commentText } }
  const fetchedRaterRef = useRef(new Set());
  const fetchedRatedUserRef = useRef(new Set());
  const fetchedRatingDetailsRef = useRef(new Set());

  const [expandedRows, setExpandedRows] = useState(new Set());

  // modal: { type: 'approve'|'deny', id }
  const [modal, setModal] = useState(null);
  const [modalInput, setModalInput] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  const [toast, setToast] = useState('');

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  }

  function nameFromUserDoc(id, data) {
    if (!data) return id.slice(0, 8);
    return data.displayName || [data.firstName, data.lastName].filter(Boolean).join(' ') || data.email || id.slice(0, 8);
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'ratingConsistencyReviews'), where('reviewStatus', '==', 'pending')),
      snap => {
        const docs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
        setReviews(docs);
        setLoading(false);

        // Secondary reads — rater display names (deduped)
        const newRaterUids = docs.map(d => d.raterId).filter(id => id && !fetchedRaterRef.current.has(id));
        if (newRaterUids.length > 0) {
          newRaterUids.forEach(id => fetchedRaterRef.current.add(id));
          Promise.all(
            newRaterUids.map(id => getDoc(doc(db, 'users', id)).then(s => [id, nameFromUserDoc(id, s.exists() ? s.data() : null)]))
          ).then(entries => setRaterNames(prev => ({ ...prev, ...Object.fromEntries(entries) })));
        }

        // Secondary reads — ratedUser (ratee) display names (deduped)
        const newRatedUserUids = docs.map(d => d.ratedUserId).filter(id => id && !fetchedRatedUserRef.current.has(id));
        if (newRatedUserUids.length > 0) {
          newRatedUserUids.forEach(id => fetchedRatedUserRef.current.add(id));
          Promise.all(
            newRatedUserUids.map(id => getDoc(doc(db, 'users', id)).then(s => [id, nameFromUserDoc(id, s.exists() ? s.data() : null)]))
          ).then(entries => setRatedUserNames(prev => ({ ...prev, ...Object.fromEntries(entries) })));
        }

        // Secondary reads — the underlying rating's date/scores/comment
        // (the ratingConsistencyReviews doc itself only holds the AI
        // verdict/reasoning + workflow state, not the rating's own content —
        // deduped by ratingId).
        const newRatingIds = docs.map(d => d.ratingId).filter(id => id && !fetchedRatingDetailsRef.current.has(id));
        if (newRatingIds.length > 0) {
          newRatingIds.forEach(id => fetchedRatingDetailsRef.current.add(id));
          Promise.all(
            newRatingIds.map(async id => {
              const [ratingSnap, commentSnap] = await Promise.all([
                getDoc(doc(db, 'ratings', id)),
                getDoc(doc(db, 'ratings', id, 'comment', 'text')),
              ]);
              const ratingData = ratingSnap.exists() ? ratingSnap.data() : {};
              return [id, {
                submittedAt: ratingData.submittedAt || null,
                answers: Array.isArray(ratingData.answers) ? ratingData.answers : [],
                commentText: commentSnap.exists() ? (commentSnap.data().value || '') : '',
              }];
            })
          ).then(entries => setRatingDetails(prev => ({ ...prev, ...Object.fromEntries(entries) })));
        }
      }
    );
    return () => unsub();
  }, []);

  function toggleExpanded(id) {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function openModal(type, id) {
    setModal({ type, id });
    setModalInput('');
    setModalError('');
  }
  function closeModal() { setModal(null); setModalInput(''); setModalError(''); }

  async function handleApprove() {
    setModalLoading(true); setModalError('');
    try {
      await httpsCallable(functions, 'reviewRatingConsistency')({ ratingId: modal.id, decision: 'approved' });
      showToast('Rating approved — will count toward the worker\'s consistency score');
      closeModal();
    } catch (err) {
      setModalError(err.message || 'Action failed');
    } finally { setModalLoading(false); }
  }

  async function handleDeny() {
    if (!modalInput.trim()) { setModalError('Reason is required'); return; }
    setModalLoading(true); setModalError('');
    try {
      await httpsCallable(functions, 'reviewRatingConsistency')({ ratingId: modal.id, decision: 'denied', reason: modalInput.trim() });
      showToast('Flag upheld — rating stays excluded');
      closeModal();
    } catch (err) {
      setModalError(err.message || 'Action failed');
    } finally { setModalLoading(false); }
  }

  const headerCell = { fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 0', fontWeight: '600' };
  const cell = { padding: '11px 0', fontSize: '12px', color: COLORS.textPrimary };
  const actionBtn = color => ({ background: 'none', border: 'none', color, fontSize: '12px', cursor: 'pointer', padding: '0 8px 0 0', fontWeight: '500' });

  return (
    <div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: COLORS.success, color: 'white',
          padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
          zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ backgroundColor: COLORS.surface, borderRadius: '12px', padding: '28px', width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>

            {modal.type === 'approve' && (
              <>
                <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '8px' }}>Approve — Comment Matches the Scores</div>
                <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '20px' }}>
                  Confirm that this comment IS consistent with the numeric scores, and the AI was wrong to flag it. This rating will count normally going forward.
                </div>
              </>
            )}

            {modal.type === 'deny' && (
              <>
                <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '8px' }}>Deny — Comment Does Not Match</div>
                <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '16px' }}>
                  Confirm the AI was right — this comment does not match the numeric scores. This rating will continue to be excluded from the worker's trust signal until reconsidered. Provide a reason.
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Reason *</label>
                  <textarea
                    value={modalInput}
                    onChange={e => setModalInput(e.target.value)}
                    placeholder="Explain why the flag is being upheld…"
                    rows={3}
                    style={{ ...INPUT_STYLE, resize: 'vertical' }}
                    autoFocus
                  />
                </div>
              </>
            )}

            {modalError && <div style={{ fontSize: '12px', color: COLORS.danger, marginBottom: '12px' }}>{modalError}</div>}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={closeModal} disabled={modalLoading} style={{ padding: '8px 16px', fontSize: '13px', border: `1px solid ${COLORS.border}`, borderRadius: '6px', backgroundColor: 'transparent', color: COLORS.textSecondary, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={modal.type === 'approve' ? handleApprove : handleDeny}
                disabled={modalLoading}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '500', border: 'none', borderRadius: '6px', cursor: 'pointer',
                  backgroundColor: modal.type === 'deny' ? COLORS.danger : COLORS.success,
                  color: 'white', opacity: modalLoading ? 0.6 : 1,
                }}
              >
                {modalLoading ? '…' : modal.type === 'approve' ? 'Approve' : 'Deny'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header row */}
      <div style={{ marginBottom: '16px' }}>
        <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>
          {loading ? '…' : reviews.length === 0
            ? 'No pending consistency flags to review'
            : `${reviews.length} rating${reviews.length !== 1 ? 's' : ''} pending review — oldest first`}
        </span>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflowX: 'auto' }}>
        <div style={{ minWidth: '960px' }}>

          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: COL, padding: '0 16px', borderBottom: `1px solid ${COLORS.border}` }}>
            {HEADERS.map(h => <div key={h} style={headerCell}>{h}</div>)}
          </div>

          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: COL, padding: '14px 16px', borderBottom: `1px solid ${COLORS.border}30` }}>
                {Array.from({ length: 7 }).map((__, j) => <div key={j}><Skeleton /></div>)}
              </div>
            ))
          ) : reviews.length === 0 ? (
            <div style={{ padding: '48px 16px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>
              No pending consistency flags to review.
            </div>
          ) : (
            reviews.map((r, i) => {
              const details = ratingDetails[r.ratingId] || {};
              const isExpanded = expandedRows.has(r.id);
              return (
                <div key={r.id}>
                  {/* Row */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: COL, padding: '0 16px', alignItems: 'center',
                    borderBottom: isExpanded ? 'none' : (i < reviews.length - 1 ? `1px solid ${COLORS.border}30` : 'none'),
                  }}>

                    {/* Rater */}
                    <div style={{ ...cell, overflow: 'hidden' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {raterNames[r.raterId] || '…'}
                      </div>
                    </div>

                    {/* Ratee */}
                    <div style={{ ...cell, overflow: 'hidden' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ratedUserNames[r.ratedUserId] || '…'}
                      </div>
                    </div>

                    {/* Rating date */}
                    <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '11px' }}>{fmtDate(details.submittedAt)}</div>

                    {/* Scores */}
                    <div
                      onClick={() => toggleExpanded(r.id)}
                      style={{ ...cell, cursor: 'pointer' }}
                    >
                      <ScoresCell answers={details.answers} />
                    </div>

                    {/* Comment (truncated, clickable) */}
                    <div
                      onClick={() => toggleExpanded(r.id)}
                      style={{
                        ...cell,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        paddingRight: '8px',
                        cursor: 'pointer',
                      }}
                    >
                      {details.commentText
                        ? truncate(details.commentText, 60)
                        : <span style={{ color: COLORS.textMuted }}>—</span>
                      }
                    </div>

                    {/* AI Verdict */}
                    <div style={cell}><VerdictCell onToggle={() => toggleExpanded(r.id)} /></div>

                    {/* Actions */}
                    <div style={{ ...cell, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button onClick={() => openModal('approve', r.id)} style={actionBtn(COLORS.success)}>Approve</button>
                      <button onClick={() => openModal('deny', r.id)} style={actionBtn(COLORS.danger)}>Deny</button>
                    </div>
                  </div>

                  {/* Expanded detail panel — full scores breakdown, full comment, full AI reasoning */}
                  {isExpanded && (
                    <div style={{
                      padding: '6px 16px 14px',
                      borderBottom: i < reviews.length - 1 ? `1px solid ${COLORS.border}30` : 'none',
                      backgroundColor: COLORS.bg + 'aa',
                    }}>
                      {details.answers && details.answers.length > 0 && (
                        <div style={{ marginBottom: '10px' }}>
                          <div style={{ fontSize: '11px', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>Scores</div>
                          {details.answers.map(a => (
                            <div key={a.questionId} style={{ fontSize: '12px', color: COLORS.textSecondary, lineHeight: '1.6' }}>
                              {a.questionTextSnapshot}: <strong style={{ color: COLORS.textPrimary }}>{a.value}/5</strong>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ fontSize: '11px', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>Comment</div>
                        <div style={{ fontSize: '12px', color: COLORS.textSecondary, lineHeight: '1.5' }}>
                          {details.commentText || <span style={{ color: COLORS.textMuted }}>No comment</span>}
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: '11px', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>AI Reasoning</div>
                        <div style={{ fontSize: '12px', color: COLORS.textSecondary, lineHeight: '1.5' }}>
                          {r.aiReasoning}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
